import React, { useState, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { Link, useNavigate } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import Skeleton from '../components/Skeleton';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Ticket,
  Users,
  Clock,
  CheckCircle2,
  TrendingUp,
  AlertCircle,
  Briefcase,
  Layers,
  Zap,
  ArrowUpRight,
  ExternalLink,
  Plus,
  Search,
  Filter,
  Activity,
  BarChart3,
  PieChart as PieIcon,
  Calendar,
  ChevronRight,
  Bug,
  Globe,
  Link as LinkIcon,
  Loader2,
  TrendingDown
} from 'lucide-react';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar
} from 'recharts';
import useSWR from 'swr';
import api from '../services/api';
import ReportModal from '../components/ReportModal';
import { useProject } from '../context/ProjectContext';

const fetcher = url => api.get(url).then(res => res.data);

const StatCard = ({ label, value, description, icon: Icon, colorClass, trend, t }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    className="glass-card p-6 border-white/5 relative overflow-hidden group hover:bg-white/[0.03] transition-all"
  >
    <div className={`absolute -right-4 -top-4 w-24 h-24 rounded-full blur-3xl opacity-10 group-hover:opacity-20 transition-opacity ${colorClass}`} />
    <div className="flex items-start justify-between mb-4">
      <div className="p-2.5 rounded-xl bg-white/5 text-slate-400 group-hover:text-white transition-colors border border-white/5">
        <Icon size={20} />
      </div>
      {trend !== undefined && (
        <div className={`flex items-center gap-1 text-[10px] font-black px-2 py-1 rounded-lg ${
          parseFloat(trend) > 0 ? 'bg-rose-500/10 text-rose-500' : 'bg-emerald-500/10 text-emerald-500'
        }`}>
          {parseFloat(trend) > 0 ? <TrendingUp size={12} /> : <TrendingDown size={12} />}
          {trend}%
        </div>
      )}
    </div>
    <div className="space-y-1">
      <div className="text-slate-500 text-[10px] font-black uppercase tracking-widest leading-none mb-1">{label}</div>
      <div className="text-3xl font-display font-black text-slate-100">{value}</div>
      {description && <div className="text-[10px] text-slate-400 font-medium opacity-60">{description}</div>}
    </div>
  </motion.div>
);

const CustomTooltip = ({ active, payload, t }) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-[#0f172a]/95 backdrop-blur-xl border border-white/10 p-4 rounded-2xl shadow-2xl">
        <p className="text-[10px] font-black text-slate-500 uppercase tracking-widest mb-2">{payload[0].payload.fullName || payload[0].payload._id || payload[0].name}</p>
        <div className="space-y-1">
          {payload.map((p, i) => (
            <div key={i} className="flex items-center justify-between gap-8">
              <div className="flex items-center gap-2">
                <div className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: p.color || p.fill }} />
                <span className="text-xs font-bold text-white capitalize">{t(p.name)}</span>
              </div>
              <span className="text-xs font-black text-white">{p.value}</span>
            </div>
          ))}
        </div>
      </div>
    );
  }
  return null;
};

const ActivityCard = React.forwardRef(({ ticket }, ref) => {
  const { t } = useLanguage();
  const status = ticket.status || 'pending';
  const title = ticket.title || ticket.message || t('untitled');

  const getStatusColor = (status, ticket = {}) => {
    if ((ticket.type === 'bug' || ticket.originalStatus === 'bug_fixing' || ticket.priority === 'urgent') && status !== 'done' && status !== 'completed') return 'text-rose-400';
    switch (status) {
      case 'done':
      case 'completed':
        return 'text-emerald-400';
      case 'review':
      case 'ready_for_review':
        return 'text-purple-400';
      case 'bug_fixing':
        return 'text-rose-400';
      case 'todo':
      case 'in_progress':
      case 'pending':
        return 'text-amber-400';
      default:
        return 'text-slate-500';
    }
  };


  const getPriorityColorClass = (priority) => {
    switch (priority) {
      case 'urgent': return 'text-[#EF4444]';
      case 'high': return 'text-[#F59E0B]';
      case 'medium': return 'text-[#6366F1]';
      case 'low': return 'text-[#10B981]';
      default: return 'text-slate-500';
    }
  };

  return (
    <motion.div
      ref={ref}
      layout
      initial={{ opacity: 0, x: -10 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, scale: 0.95 }}
      style={{
        '--status-color-hover':
          (status === 'bug_fixing' || ticket.type === 'bug' || ticket.priority === 'urgent') ? '#fb7185' : // rose-400
            (status === 'done' || status === 'completed') ? '#34d399' : // emerald-400
              (status === 'review' || status === 'ready_for_review') ? '#c084fc' : // purple-400
                (status === 'todo' || status === 'in_progress' || status === 'pending') ? '#fbbf24' : // amber-400
                  '#94a3b8' // slate-400
      }}
      className={`p-4 rounded-2xl hover:bg-[var(--status-color-hover)]/[0.03] transition-all cursor-pointer group border border-transparent hover:border-[var(--status-color-hover)]/20 relative overflow-hidden`}
    >
      <div className="flex items-start gap-4">
        <div className={`mt-2 flex items-center justify-center ${getStatusColor(status, ticket)} transition-colors group-hover:text-[var(--status-color-hover)]`}>
          {(status === 'bug_fixing' || ticket.type === 'bug' || ticket.originalStatus === 'bug_fixing' || ticket.priority === 'urgent') ? (
            <Bug size={14} fill="currentColor" fillOpacity={0.2} className="drop-shadow-[0_0_8px_currentColor]" />
          ) : (
            <div className="w-1.5 h-1.5 rounded-full shadow-[0_0_8px_currentColor] bg-current" />
          )}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between mb-1">
            <h4 className="font-bold text-slate-200 truncate group-hover:text-[var(--status-color-hover)] transition-colors text-sm flex items-center gap-2">
              <span className="text-[10px] font-black uppercase tracking-tighter bg-white/5 px-2 py-0.5 rounded border border-white/5 text-slate-500">
                {ticket.key || `#${ticket._id.slice(-6).toUpperCase()}`}
              </span>
              <span className="truncate">{title}</span>
            </h4>
            <span className="text-[10px] text-slate-500 whitespace-nowrap flex items-center gap-2">
              {ticket.focusAreas && ticket.focusAreas.length > 0 && (
                <div className="flex gap-1 items-center mr-1">
                  {ticket.focusAreas.includes('backend') && <Globe size={11} className="text-blue-400" />}
                  {ticket.focusAreas.includes('frontend') && <Layers size={11} className="text-emerald-400" />}
                  {ticket.focusAreas.includes('ux') && <LinkIcon size={11} className="text-fuchsia-400" />}
                </div>
              )}
              {new Date(ticket.createdAt).toLocaleDateString(useLanguage().lang === 'es' ? 'es-ES' : 'en-US', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
            </span>
          </div>
          <div className="flex items-center gap-3 text-[10px] uppercase font-black tracking-tighter">
            <span className="text-slate-500">{t('status')}:</span>
            <span className={getStatusColor(status, ticket)}>{t(status)}</span>
            <span className="w-1 h-1 rounded-full bg-slate-700" />
            <span className="text-slate-500">{t('priority')}:</span>
            <span className={getPriorityColorClass(ticket.priority || 'medium')}>{t(ticket.priority || 'medium')}</span>
            <span className="w-1 h-1 rounded-full bg-slate-700" />
            <span className="text-[10px] text-slate-500 font-medium">{ticket.creator?.name?.split(' ')[0] || t('admin')}</span>
          </div>
        </div>
      </div>
    </motion.div>
  );
});

const NoDataPlaceholder = ({ title, description }) => {
  const { t } = useLanguage();
  return (
    <div className="flex flex-col items-center justify-center p-12 text-center space-y-4 bg-white/[0.01] border border-white/5 rounded-3xl h-full min-h-[250px] animate-in fade-in duration-500">
      <div className="w-16 h-16 rounded-full bg-slate-500/5 flex items-center justify-center text-slate-500/20 border border-slate-500/10">
        <BarChart3 size={32} />
      </div>
      <div className="max-w-[200px]">
        <h4 className="text-slate-300 font-bold text-sm tracking-tight">{title || t('noDataTitle')}</h4>
        <p className="text-slate-500 text-[10px] font-medium leading-relaxed mt-1">
          {description || t('noDataDesc')}
        </p>
      </div>
    </div>
  );
};

const ActivitySkeleton = () => (
  <div className="p-4 rounded-2xl border border-white/5 bg-white/[0.01] flex items-start gap-4">
    <Skeleton width="16px" height="16px" borderRadius="50%" className="mt-2" />
    <div className="flex-1 space-y-3">
      <div className="flex justify-between items-center">
        <Skeleton width="40%" height="14px" />
        <Skeleton width="20%" height="10px" />
      </div>
      <div className="flex gap-2">
        <Skeleton width="50px" height="8px" />
        <Skeleton width="50px" height="8px" />
        <Skeleton width="50px" height="8px" />
      </div>
    </div>
  </div>
);

const StatCardSkeleton = () => (
  <div className="glass-card p-6 border-white/5 relative overflow-hidden">
    <div className="flex items-start justify-between mb-4">
      <Skeleton width="40px" height="40px" borderRadius="0.75rem" />
      <Skeleton width="40px" height="20px" borderRadius="0.5rem" />
    </div>
    <div className="space-y-3">
      <Skeleton width="60px" height="8px" />
      <Skeleton width="100px" height="32px" />
      <Skeleton width="140px" height="12px" />
    </div>
  </div>
);

const DashboardOverviewSkeleton = ({ t }) => (
  <div className="space-y-10 py-6">
    <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 px-2">
      <div className="space-y-3">
        <Skeleton width="120px" height="12px" />
        <Skeleton width="300px" height="40px" />
      </div>
      <Skeleton width="48px" height="48px" borderRadius="0.75rem" />
    </div>

    <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
      <div className="lg:col-span-8 space-y-8">
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-6">
          {[1, 2, 3, 4].map(i => <StatCardSkeleton key={i} />)}
        </div>
        <div className="glass-card p-8 border-white/5 bg-white/[0.01] h-[400px]">
          <Skeleton width="150px" height="12px" className="mb-8" />
          <Skeleton width="100%" height="280px" />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          <div className="glass-card p-8 border-white/5 bg-white/[0.01] h-[300px]">
            <Skeleton width="150px" height="12px" className="mb-8" />
            <div className="space-y-6">
              {[1, 2, 3].map(i => (
                <div key={i} className="flex gap-4 items-center">
                  <Skeleton width="32px" height="32px" borderRadius="0.75rem" />
                  <div className="flex-1 space-y-2">
                    <Skeleton width="100px" height="10px" />
                    <Skeleton width="100%" height="8px" />
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="glass-card p-8 border-white/5 bg-white/[0.01] h-[300px] flex flex-col items-center justify-center">
            <Skeleton width="150px" height="12px" className="mb-8 self-start" />
            <Skeleton width="160px" height="160px" borderRadius="50%" />
          </div>
        </div>
      </div>
      <div className="lg:col-span-4 space-y-8">
        <div className="glass-card p-6 border-white/5">
          <Skeleton width="120px" height="18px" className="mb-6" />
          <div className="space-y-4">
            {[1, 2, 3, 4, 5, 6].map(i => <ActivitySkeleton key={i} />)}
          </div>
        </div>
      </div>
    </div>
  </div>
);

const DashboardOverview = () => {
  const { t } = useLanguage();
  const { canAccess, projects: teams, loading: isLoadingProjects, activeProjectId } = useProject();
  const navigate = useNavigate();
  const [showReport, setShowReport] = useState(false);

  useEffect(() => {
    // If user has no projects at all, redirect to projects page for onboarding
    if (!isLoadingProjects && teams.length === 0) {
      navigate('/projects');
      return;
    }

    if (!canAccess('/')) {
      navigate('/projects');
    }
  }, [canAccess, navigate, isLoadingProjects, teams]);

  // Smooth scroll to top when report opens
  useEffect(() => {
    if (showReport) {
      const mainElement = document.querySelector('main');
      if (mainElement) {
        mainElement.scrollTo({ top: 0, behavior: 'smooth' });
      }
    }
  }, [showReport]);

  // SWR calls
  const { data: ticketsData, isLoading: isLoadingTickets } = useSWR(activeProjectId ? `/tickets?projectId=${activeProjectId}&limit=8` : null, fetcher, {
    refreshInterval: 60000
  });

  const { data: stats, isLoading: isLoadingStats } = useSWR(activeProjectId ? `/stats/team/${activeProjectId}` : null, fetcher, {
    refreshInterval: 60000
  });

  const tickets = ticketsData?.data || [];
  const isInitialLoading = isLoadingProjects || (activeProjectId && (isLoadingTickets || isLoadingStats));

  const priorityData = useMemo(() => {
    if (!stats?.priorityDistribution || Object.keys(stats.priorityDistribution).length === 0) return [];
    return Object.entries(stats.priorityDistribution).map(([name, value]) => ({
      id: name,
      name: t(name),
      value
    }));
  }, [stats, t]);

  const statusData = useMemo(() => {
    if (!stats?.statusDistribution || Object.keys(stats.statusDistribution).length === 0) return [];
    return Object.entries(stats.statusDistribution).map(([name, value]) => ({
      id: name,
      name: t(name),
      value
    }));
  }, [stats, t]);

  const workloadData = useMemo(() => {
    const creator = stats?.creatorDistribution || {};
    const resolver = stats?.resolverDistribution || {};
    if (Object.keys(creator).length === 0 && Object.keys(resolver).length === 0) return [];

    const allNames = new Set([...Object.keys(creator), ...Object.keys(resolver)]);
    return Array.from(allNames)
      .map(name => ({
        name: name.split(' ')[0],
        fullName: name,
        created: creator[name] || 0,
        resolved: resolver[name] || 0,
        total: (creator[name] || 0) + (resolver[name] || 0),
      }))
      .sort((a, b) => b.total - a.total)
      .slice(0, 5);
  }, [stats]);


  const getBacklogInsight = () => {
    if (!stats?.insights || stats?.summary?.backlogCount === 0) return t('backlogHealthy');

    const { creationRate, resolutionRate, isGrowing, isStable } = stats.insights;

    if (isGrowing) {
      return t('backlogGrowing')
        .replace('{{created}}', creationRate)
        .replace('{{resolved}}', resolutionRate);
    }

    if (isStable) {
      return t('backlogStable')
        .replace('{{rate}}', creationRate);
    }

    return t('backlogImproving')
      .replace('{{resolved}}', resolutionRate)
      .replace('{{created}}', creationRate);
  };

  const hasTickets = stats?.summary?.totalTickets > 0;
  const completionRate = stats?.summary?.totalTickets 
    ? Math.round(((stats.summary.closedTickets || 0) / stats.summary.totalTickets) * 100) 
    : 0;

  if (isInitialLoading) return <DashboardOverviewSkeleton t={t} />;

  if (teams.length === 0 && !isLoadingProjects) {
    return (
      <div className="flex flex-col items-center justify-center h-[calc(100vh-160px)] space-y-8 animate-in fade-in duration-700">
        <div className="w-24 h-24 rounded-3xl bg-primary/10 flex items-center justify-center text-primary shadow-[0_0_50px_rgba(37,99,235,0.2)]">
          <Briefcase size={48} strokeWidth={1.5} />
        </div>
        <div className="text-center max-w-md">
          <h2 className="text-3xl font-display font-black text-slate-100 mb-4">{t('startProjectTitle')}</h2>
          <p className="text-slate-500 text-lg leading-relaxed">{t('startProjectDesc')}</p>
        </div>
        <a href="/projects" className="px-8 py-4 bg-primary text-white rounded-2xl font-black uppercase tracking-widest text-sm hover:bg-blue-600 transition-all shadow-xl shadow-primary/30 hover:scale-105 active:scale-95">
          {t('createFirstProject')}
        </a>
      </div>
    );
  }

  return (
    <div className="space-y-10 py-6">
      {/* Header Section */}
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 px-2">
        <div>
          <div className="flex items-center gap-2 text-primary font-black uppercase tracking-[0.2em] text-[10px] mb-2">
            <Activity size={12} /> {t('liveDashboard')}
          </div>
          <h1 className="text-4xl font-display font-black text-slate-100">
            {t('project')} <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary to-blue-400">{t('insight')}</span>
          </h1>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <Link to="/projects" className="p-2.5 rounded-xl bg-primary/10 text-primary hover:bg-primary hover:text-white transition-all">
            <Plus size={20} />
          </Link>
        </div>
      </div>

      {!isInitialLoading && !hasTickets ? (
        <div className="grid grid-cols-1 gap-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            className="glass-card p-12 overflow-hidden relative group border-white/5 flex flex-col items-center text-center space-y-8"
          >
            <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-primary/50 to-transparent group-hover:via-primary transition-all duration-1000" />
            <div className="w-20 h-20 rounded-3xl bg-primary/5 flex items-center justify-center text-primary relative">
              <div className="absolute inset-0 bg-primary/20 blur-2xl rounded-full animate-pulse" />
              <Layers size={40} className="relative z-10" />
            </div>
            
            <div className="max-w-md space-y-3">
              <h2 className="text-2xl font-display font-black text-white italic">{t('noActiveBacklogTitle')}</h2>
              <p className="text-slate-400 text-sm font-medium leading-relaxed italic">
                {t('noActiveBacklogDesc')}
              </p>
            </div>

            <div className="flex flex-wrap items-center justify-center gap-4">
              <Link
                to="/backlog"
                className="px-8 py-3 rounded-xl bg-primary text-white font-black uppercase tracking-widest text-[10px] hover:bg-blue-600 transition-all shadow-lg shadow-primary/20 hover:scale-105 active:scale-95"
              >
                {t('goToBacklog')}
              </Link>
              <button
                onClick={() => navigate('/projects')}
                className="px-8 py-3 rounded-xl bg-white/5 text-white font-black uppercase tracking-widest text-[10px] hover:bg-white/10 transition-all border border-white/10"
              >
                {t('changeProject')}
              </button>
            </div>
          </motion.div>
        </div>
      ) : !isInitialLoading && (
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          {/* Main Content: Stats Groups */}
          <div className="lg:col-span-8 space-y-8">
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-6">
              <StatCard
                label={t('activeBacklog')}
                value={stats?.summary?.backlogCount || 0}
                icon={Layers}
                colorClass="bg-blue-500"
                t={t}
              />
              <StatCard
                label={t('resolvedBugs')}
                value={stats?.summary?.closedBugs || 0}
                icon={Bug}
                colorClass="bg-emerald-500"
                t={t}
              />
              <StatCard
                label={t('weeklyGrowth')}
                value={`${stats?.summary?.weeklyGrowth || 0}%`}
                icon={TrendingUp}
                colorClass="bg-rose-500"
                trend={stats?.summary?.weeklyGrowth}
                t={t}
              />
              <StatCard
                label={t('completionRate')}
                value={`${completionRate}%`}
                icon={CheckCircle2}
                colorClass="bg-amber-500"
                description={t('efficiencyRate')}
                t={t}
              />
            </div>

            {/* Trends Chart */}
            <div className="glass-card p-8 border-white/5 bg-white/[0.01]">
                <div className="flex items-center justify-between mb-8">
                  <h3 className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-500">{t('velocityTrends')}</h3>
                  <div className="flex items-center gap-4">
                    <div className="flex items-center gap-1.5">
                      <div className="w-2.5 h-2.5 rounded-full bg-blue-500" />
                      <span className="text-[10px] font-black uppercase tracking-wider text-slate-400">{t('createdLabel')}</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <div className="w-2.5 h-2.5 rounded-full bg-emerald-500" />
                      <span className="text-[10px] font-black uppercase tracking-wider text-slate-400">{t('resolvedLabel')}</span>
                    </div>
                  </div>
                </div>
                <div className="h-[300px]">
                  {stats?.dailyActivity && stats?.dailyActivity.length > 0 ? (
                    <ResponsiveContainer width="100%" height="100%">
                      <AreaChart data={stats.dailyActivity}>
                        <defs>
                          <linearGradient id="colorCreated" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#2563eb" stopOpacity={0.3}/>
                            <stop offset="95%" stopColor="#2563eb" stopOpacity={0}/>
                          </linearGradient>
                          <linearGradient id="colorResolved" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#10b981" stopOpacity={0.3}/>
                            <stop offset="95%" stopColor="#10b981" stopOpacity={0}/>
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="#ffffff10" vertical={false} />
                        <XAxis 
                          dataKey="_id" 
                          axisLine={false} 
                          tickLine={false} 
                          tick={{ fill: '#64748b', fontSize: 10 }}
                          tickFormatter={(val) => val.split('-').slice(1).join('/')}
                          dy={10}
                        />
                        <YAxis 
                          axisLine={false} 
                          tickLine={false} 
                          tick={{ fill: '#64748b', fontSize: 10 }} 
                        />
                        <Tooltip content={<CustomTooltip t={t} />} />
                        <Area type="monotone" dataKey="created" stroke="#2563eb" fillOpacity={1} fill="url(#colorCreated)" strokeWidth={3} />
                        <Area type="monotone" dataKey="resolved" stroke="#10b981" fillOpacity={1} fill="url(#colorResolved)" strokeWidth={3} />
                      </AreaChart>
                    </ResponsiveContainer>
                  ) : (
                    <NoDataPlaceholder />
                  )}
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="glass-card p-8 border-white/5 bg-white/[0.01]">
                <div className="flex items-center justify-between mb-8">
                  <h3 className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-500">{t('workloadDistribution')}</h3>
                  <div className="flex items-center gap-4">
                    <div className="flex items-center gap-1.5">
                      <div className="w-2.5 h-2.5 rounded-full bg-blue-500/60" />
                      <span className="text-[10px] font-black uppercase tracking-wider text-slate-400">{t('createdLabel')}</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <div className="w-2.5 h-2.5 rounded-full bg-emerald-500" />
                      <span className="text-[10px] font-black uppercase tracking-wider text-slate-400">{t('resolvedLabel')}</span>
                    </div>
                  </div>
                </div>
                <div className="space-y-6">
                  {workloadData.length > 0 ? (
                    <div className="space-y-6">
                      {workloadData.map((w, idx) => {
                        const maxVal = Math.max(...workloadData.map(item => item.total), 1);
                        const createdPct = (w.created / maxVal) * 100;
                        const resolvedPct = (w.resolved / maxVal) * 100;
                        
                        return (
                          <motion.div 
                            key={w.fullName} 
                            initial={{ opacity: 0, x: -20 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: idx * 0.1 }}
                            className="group/workload"
                          >
                            <div className="flex items-center justify-between mb-2">
                              <div className="flex items-center gap-3">
                                <div className="w-8 h-8 rounded-xl bg-primary/10 text-primary flex items-center justify-center text-[10px] font-black border border-white/10 group-hover/workload:bg-primary group-hover/workload:text-white transition-all">
                                  {w.name[0]}
                                </div>
                                <span className="text-sm font-bold text-slate-300 group-hover/workload:text-white transition-colors">
                                  {w.fullName}
                                </span>
                              </div>
                              <div className="flex items-center gap-2 text-[10px] font-black">
                                <span className="text-blue-400">{w.created}</span>
                                <span className="text-slate-700">/</span>
                                <span className="text-emerald-400">{w.resolved}</span>
                              </div>
                            </div>
                            <div className="space-y-1.5 pl-11">
                               <div className="h-1.5 bg-white/5 rounded-full overflow-hidden relative border border-white/5">
                                  <motion.div 
                                    initial={{ width: 0 }}
                                    animate={{ width: `${createdPct}%` }}
                                    className="h-full bg-blue-500/60 rounded-full" 
                                    transition={{ duration: 1, delay: 0.5 + idx * 0.1 }}
                                  />
                               </div>
                               <div className="h-1.5 bg-white/5 rounded-full overflow-hidden relative border border-white/5">
                                  <motion.div 
                                    initial={{ width: 0 }}
                                    animate={{ width: `${resolvedPct}%` }}
                                    className="h-full bg-emerald-500 rounded-full shadow-[0_0_10px_rgba(16,185,129,0.3)]" 
                                    transition={{ duration: 1, delay: 0.7 + idx * 0.1 }}
                                  />
                               </div>
                            </div>
                          </motion.div>
                        );
                      })}
                    </div>
                  ) : (
                    <NoDataPlaceholder />
                  )}
                </div>
              </div>

              <div className="glass-card p-8 border-white/5 bg-white/[0.01]">
                <div className="flex items-center justify-between mb-8">
                  <h3 className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-500">{t('statusDistribution')}</h3>
                </div>
                <div className="h-[250px]">
                  {statusData.length > 0 ? (
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Tooltip content={<CustomTooltip t={t} />} />
                        <Pie
                          data={statusData}
                          innerRadius={60}
                          outerRadius={80}
                          paddingAngle={8}
                          dataKey="value"
                          stroke="none"
                        >
                          {statusData.map((entry, index) => {
                            let color = '#2563eb';
                            if (entry.id === 'done' || entry.id === 'completed') color = '#10b981';
                            if (entry.id === 'review' || entry.id === 'ready_for_review') color = '#a855f7';
                            if (entry.id === 'bug_fixing') color = '#f43f5e';
                            if (entry.id === 'todo' || entry.id === 'in_progress' || entry.id === 'pending') color = '#f59e0b';
                            return <Cell key={`cell-${index}`} fill={color} className="hover:opacity-80 transition-opacity cursor-pointer focus:outline-none" />;
                          })}
                        </Pie>
                        <text
                          x="50%"
                          y="50%"
                          textAnchor="middle"
                          dominantBaseline="middle"
                          className="fill-slate-500 font-black text-[10px] uppercase tracking-tighter"
                        >
                          {t('distribution')}
                        </text>
                      </PieChart>
                    </ResponsiveContainer>
                  ) : (
                    <NoDataPlaceholder />
                  )}
                </div>
              </div>

              <div className="glass-card p-8 border-white/5 bg-white/[0.01] md:col-span-2">
                <div className="flex items-center justify-between mb-8">
                  <h3 className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-500">{t('priorityDistribution')}</h3>
                </div>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
                  <div className="h-[250px]">
                    {priorityData.length > 0 ? (
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie data={priorityData} innerRadius={60} outerRadius={80} paddingAngle={10} dataKey="value" stroke="none">
                            {priorityData.map((entry, index) => {
                              let color = '#64748b';
                              if (entry.id === 'urgent') color = '#f43f5e';
                              if (entry.id === 'high') color = '#f59e0b';
                              if (entry.id === 'medium') color = '#2563eb';
                              if (entry.id === 'low') color = '#10b981';
                              return <Cell key={`cell-${index}`} fill={color} />;
                            })}
                          </Pie>
                          <Tooltip content={<CustomTooltip t={t} />} />
                        </PieChart>
                      </ResponsiveContainer>
                    ) : (
                      <NoDataPlaceholder />
                    )}
                  </div>
                  <div className="space-y-4">
                    {priorityData.map((p, i) => (
                      <div key={p.id} className="flex items-center justify-between p-4 rounded-2xl bg-white/[0.02] border border-white/5">
                        <div className="flex items-center gap-3">
                          <div className="w-2 h-2 rounded-full" style={{ 
                            backgroundColor: 
                              p.id === 'urgent' ? '#f43f5e' : 
                              p.id === 'high' ? '#f59e0b' : 
                              p.id === 'medium' ? '#2563eb' : 
                              p.id === 'low' ? '#10b981' : '#64748b' 
                          }} />
                          <span className="text-xs font-bold text-slate-300 capitalize">{p.name}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-xs font-black text-white">{p.value}</span>
                          <span className="text-[8px] font-black text-slate-600 uppercase tracking-tighter">{t('tickets')}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Sidebar: Recent Activity & Insight */}
          <div className="lg:col-span-4 space-y-8">
            <section className="glass-card p-6 border-white/5">
              <div className="flex items-center justify-between mb-6">
                <h3 className="font-bold text-slate-200 flex items-center gap-2">
                  <Activity size={18} className="text-rose-500" />
                  {t('liveActivity')}
                </h3>
                <Link to="/board" className="text-[10px] font-black uppercase text-primary hover:text-white transition-colors">{t('seeAll')}</Link>
              </div>
              <div className="space-y-2 pr-2 custom-scrollbar">
                {tickets.length > 0 ? (
                  <AnimatePresence mode='popLayout'>
                    {tickets.map(ticket => (
                      <ActivityCard key={ticket._id} ticket={ticket} />
                    ))}
                  </AnimatePresence>
                ) : (
                  <div className="py-20 text-center">
                    <p className="text-[10px] font-black uppercase text-slate-600 tracking-widest">{t('noActivityDetected')}</p>
                  </div>
                )}
              </div>
            </section>

            <div className="glass-card p-6 border-primary/20 bg-primary/5 relative overflow-hidden group">
              <div className="absolute top-0 right-0 -mr-8 -mt-8 w-32 h-32 bg-primary/20 blur-3xl rounded-full" />
              <div className="relative z-10">
                <div className="inline-flex items-center gap-2 px-2 py-1 rounded-md bg-primary/20 text-primary text-[9px] font-black uppercase tracking-widest mb-4">
                  <Zap size={10} /> {t('poInsight')}
                </div>
                <p className="text-sm text-slate-200 font-medium leading-relaxed mb-4">
                  {getBacklogInsight()}
                </p>
                <button
                  onClick={() => setShowReport(true)}
                  className="flex items-center gap-2 text-primary font-bold text-xs group/btn"
                >
                  {t('generateFullReport')} <ChevronRight size={14} className="group-hover/btn:translate-x-1 transition-transform" />
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {createPortal(
        <AnimatePresence mode='wait'>
          {showReport && (
            <ReportModal
              key="dashboard-report-modal"
              isOpen={showReport}
              onClose={() => setShowReport(false)}
              stats={stats}
              tickets={tickets}
              teamName={teams.find(tm => tm._id === activeProjectId)?.name || 'Project'}
              projectId={activeProjectId}
              t={t}
            />
          )}
        </AnimatePresence>,
        document.body
      )}
    </div>
  );
};

export default DashboardOverview;
